import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AdminHeader from "../../header/admin_header";
import AdminSidebar from "../../sidebar/admin_sidebar";
import { CALCULATION_METHODS, EMISSION_SCOPES } from "../KPIGrid/model/options";
import { useDebouncedValue } from "../KPIGrid/model/useDebouncedValue";
import { PAGE_SIZE, useEmissionFactorBreakdown } from "./useEmissionFactorBreakdown";
import { useFilterOptions } from "./useFilterOptions";
import KpiFactorCard from "./KpiFactorCard";
import ReferencesPanel from "./ReferencesPanel";
import ReferenceApplyBar from "./ReferenceApplyBar";
import { useGhgReferences } from "./useGhgReferences";
import { applyFactorReference } from "./referencesApi";
import {
  EMPTY_SELECTION,
  buildReferenceChange,
  isFactorSelected,
  selectedCount,
  toggleFactor,
  toggleFactors,
} from "./referenceHelpers";
import { KG_CO2E } from "../../../_constants/emissionUnits";
import "../KPIGrid/KPIGrid.css";
import "./EmissionFactorBreakdown.css";

/**
 * Every KPI's emission factors, gas by gas, filtered by KPI title, scope,
 * source and method, loaded a page of KPIs at a time with totals over every
 * match. Factors are edited from the KPI Grid; here each value's reference is
 * kept: pick factors one by one, a KPI at a time or every match of the
 * filters, and apply one reference to their emission factors, GWPs, NCVs or
 * densities at once. The references panel keeps the library and the
 * reference of each GHG database. Modules and sources only label rows and
 * fill the filters, so when either list fails to load the page still
 * renders, with a note and a retry.
 */
export default function EmissionFactorBreakdown() {
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("");
  const [databaseId, setDatabaseId] = useState("");
  const [method, setMethod] = useState("");
  const [openIds, setOpenIds] = useState(() => new Set());

  const settledSearch = useDebouncedValue(search.trim());
  const filters = useMemo(
    () => ({ search: settledSearch, scope, databaseId, method }),
    [settledSearch, scope, databaseId, method],
  );
  const { kpis, totalKpis, totals, loading, loadingMore, error, reload, loadMore } = useEmissionFactorBreakdown(filters);
  const { modules, databases, error: filterError, reload: reloadFilters } = useFilterOptions();
  const { references, error: referencesError, reload: reloadReferences } = useGhgReferences();
  const [selection, setSelection] = useState(EMPTY_SELECTION);

  const loadedIds = useMemo(() => kpis.flatMap((kpi) => kpi.factors.map((factor) => factor.id)), [kpis]);
  const isSelected = useCallback((factorId) => isFactorSelected(selection, factorId), [selection]);
  const selectFactor = useCallback(
    (factorId) => setSelection((current) => toggleFactor(current, factorId, loadedIds)),
    [loadedIds],
  );
  const selectKpi = useCallback(
    (kpi) =>
      setSelection((current) =>
        toggleFactors(
          current,
          kpi.factors.map((factor) => factor.id),
          loadedIds,
        ),
      ),
    [loadedIds],
  );
  const pickedCount = selectedCount(selection, totals.factors);
  const selectAllMatching = () => setSelection({ allMatching: true, ids: new Set() });

  /**
   * A filter change starts the selection over, so "every match" never
   * reaches factors the screen no longer shows.
   *
   * @param {(value: string) => void} setter
   * @returns {(e: { target: { value: string } }) => void}
   */
  const onFilter = (setter) => (e) => {
    setter(e.target.value);
    setSelection(EMPTY_SELECTION);
  };

  /**
   * Applies the change, then starts the selection over: the list reloads from
   * its first page, so a selection kept past it would point at factors no
   * longer on screen.
   *
   * @param {number|null} referenceId
   * @param {string[]} targets
   * @returns {Promise<boolean>}
   */
  const applyReference = async (referenceId, targets) => {
    const res = await applyFactorReference(buildReferenceChange(selection, filters, referenceId, targets));
    if (!res?.isSuccess) return false;
    setSelection(EMPTY_SELECTION);
    reload();
    reloadReferences();
    return true;
  };

  const refreshReferences = () => {
    reloadReferences();
    reload();
  };

  const moduleNameById = useMemo(
    () => new Map(modules.map((module) => [Number(module.id), module.title])),
    [modules],
  );

  const toggle = useCallback((kpiId) => {
    setOpenIds((current) => {
      const next = new Set(current);
      if (next.has(kpiId)) next.delete(kpiId);
      else next.add(kpiId);
      return next;
    });
  }, []);

  const allOpen = kpis.length > 0 && kpis.every((kpi) => openIds.has(kpi.kpi_id));
  const toggleAll = () => setOpenIds(allOpen ? new Set() : new Set(kpis.map((kpi) => kpi.kpi_id)));
  const remaining = totalKpis - kpis.length;

  return (
    <div>
      <AdminSidebar dataFromParent={location.pathname} />
      <AdminHeader />
      <div className="main_wrapper">
        <div className="inner_wraapper">
          <div className="kgb-root efb-root">
            <h1 className="kgb-title">Emission Factors</h1>
            <p className="efb-lead">
              Every KPI's emission factors, gas by gas, and where each value is quoted from. Add or change factors
              from the KPI Grid's Emission factors column.
            </p>

            <ReferencesPanel
              references={references}
              error={referencesError}
              onReferencesChanged={refreshReferences}
            />

            <div className="kgb-card efb-filters">
              <label className="efb-field efb-field--grow">
                <span>KPI</span>
                <input
                  type="search"
                  value={search}
                  placeholder="Search KPI title"
                  onChange={onFilter(setSearch)}
                />
              </label>
              <label className="efb-field">
                <span>Scope</span>
                <select value={scope} onChange={onFilter(setScope)}>
                  <option value="">All scopes</option>
                  {EMISSION_SCOPES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="efb-field">
                <span>Source</span>
                <select value={databaseId} onChange={onFilter(setDatabaseId)}>
                  <option value="">All sources</option>
                  {databases.map((database) => (
                    <option key={database.id} value={String(database.id)}>
                      {database.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="efb-field">
                <span>Method</span>
                <select value={method} onChange={onFilter(setMethod)}>
                  <option value="">All methods</option>
                  {CALCULATION_METHODS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {filterError ? (
              <div className="efb-filter-note" role="status">
                {filterError}{" "}
                <button type="button" className="kgb-btn kgb-btn--quiet" onClick={reloadFilters}>
                  Retry
                </button>
              </div>
            ) : null}

            <div className="efb-stats" aria-live="polite">
              <div className="efb-stat">
                <span className="efb-stat-value">{totalKpis.toLocaleString("en-IN")}</span>
                <span className="efb-stat-label">KPIs with factors</span>
              </div>
              <div className="efb-stat">
                <span className="efb-stat-value">{totals.factors.toLocaleString("en-IN")}</span>
                <span className="efb-stat-label">Factors</span>
              </div>
              <div className="efb-stat">
                <span className="efb-stat-value">{totals.gas_wise.toLocaleString("en-IN")}</span>
                <span className="efb-stat-label">Built gas-wise</span>
              </div>
              <div className="efb-stat">
                <span className="efb-stat-value">{totals.fuel_energy.toLocaleString("en-IN")}</span>
                <span className="efb-stat-label">Fuel energy (density × NCV)</span>
              </div>
            </div>

            <div className="efb-legend">
              <span className="efb-legend-item efb-legend-item--co2">CO₂</span>
              <span className="efb-legend-item efb-legend-item--ch4">CH₄</span>
              <span className="efb-legend-item efb-legend-item--n2o">N₂O</span>
              <span className="efb-muted">share of each factor's {KG_CO2E}</span>
              {kpis.length ? (
                <button type="button" className="kgb-btn kgb-btn--quiet efb-expand-all" onClick={toggleAll}>
                  {allOpen ? "Collapse all" : "Expand all"}
                </button>
              ) : null}
            </div>

            {pickedCount ? (
              <ReferenceApplyBar
                count={pickedCount}
                allMatching={selection.allMatching}
                totalFactors={totals.factors}
                references={references}
                onSelectAllMatching={selectAllMatching}
                onClear={() => setSelection(EMPTY_SELECTION)}
                onApply={applyReference}
                onReferencesChanged={reloadReferences}
              />
            ) : totals.factors ? (
              <div className="efb-selecthint">
                Tick factors, or a whole KPI, to give their values a reference.{" "}
                <button type="button" className="kgb-btn kgb-btn--quiet" onClick={selectAllMatching}>
                  Select all {totals.factors.toLocaleString("en-IN")} matching the filters
                </button>
              </div>
            ) : null}

            {error ? (
              <div className="efb-message efb-message--error">
                {error}{" "}
                <button type="button" className="kgb-btn kgb-btn--quiet" onClick={reload}>
                  Retry
                </button>
              </div>
            ) : null}
            {!error && loading && !kpis.length ? <div className="efb-message">Loading emission factors…</div> : null}
            {!error && !loading && !kpis.length ? (
              <div className="efb-message">No emission factors match these filters.</div>
            ) : null}

            <div className={`efb-list${loading ? " efb-list--loading" : ""}`}>
              {kpis.map((kpi) => (
                <KpiFactorCard
                  key={kpi.kpi_id}
                  kpi={kpi}
                  moduleName={moduleNameById.get(Number(kpi.module_id)) || ""}
                  open={openIds.has(kpi.kpi_id)}
                  onToggle={toggle}
                  isSelected={isSelected}
                  onSelectFactor={selectFactor}
                  onSelectKpi={selectKpi}
                />
              ))}
            </div>

            {!loading && remaining > 0 ? (
              <div className="efb-more">
                <button type="button" className="kgb-btn" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : `Show ${Math.min(PAGE_SIZE, remaining)} more of ${remaining}`}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
